import { useCallback, useState, useSyncExternalStore } from 'react'
import {
    browserNotificationsSupported,
    getBrowserNotificationsEnabled,
    notificationPermission,
    requestBrowserNotificationPermission,
    setBrowserNotificationsEnabled,
    subscribeToBrowserNotifications,
    type BrowserNotificationPermission,
} from '../lib/browser-notifications'

export interface BrowserNotificationsState {
    supported: boolean
    permission: BrowserNotificationPermission
    enabled: boolean
    enable: () => Promise<boolean>
    disable: () => void
    toggle: () => Promise<boolean>
}

export function useBrowserNotifications(): BrowserNotificationsState {
    const [, refresh] = useState(0)
    const stored = useSyncExternalStore(
        subscribeToBrowserNotifications,
        getBrowserNotificationsEnabled,
        getBrowserNotificationsEnabled,
    )
    const supported = browserNotificationsSupported()
    const permission = notificationPermission()

    const enable = useCallback(async () => {
        const answer = await requestBrowserNotificationPermission()
        setBrowserNotificationsEnabled(answer === 'granted')
        refresh((n) => n + 1)

        return answer === 'granted'
    }, [])

    const disable = useCallback(() => {
        setBrowserNotificationsEnabled(false)
        refresh((n) => n + 1)
    }, [])

    const toggle = useCallback(async () => {
        if (supported && permission === 'granted' && stored) {
            disable()

            return false
        }

        return enable()
    }, [disable, enable, permission, stored, supported])

    return {
        supported,
        permission,
        enabled: supported && permission === 'granted' && stored,
        enable,
        disable,
        toggle,
    }
}
